
import React, { PureComponent } from 'react';

import { safePlayMediaElement } from '../../../../base/media/playbackUtils';
import logger from '../../../logger';

import AudioSettingsEntry from './AudioSettingsEntry';
import TestButton from './TestButton';

const TEST_SOUND_PATH = 'sounds/ring.wav';

/**
 * The type of the React {@code Component} props of {@link SpeakerEntry}.
 */
type Props = {

    /**
     * The text label for the entry.
     */
    children: React$Node,

    /**
     * Flag controlling the selection state of the entry.
     */
    isSelected: boolean,

    /**
     * The deviceId of the speaker.
     */
    deviceId: string,

    /**
     * Click handler for the component.
     */
    onClick: Function,
};

/**
 * Implements a React {@link Component} which displays an audio
 * output settings entry. The user can click and play a test sound.
 *
 * @augments Component
 */
export default class SpeakerEntry extends PureComponent<Props> {
    /**
     * A React ref to the HTML element containing the {@code audio} instance.
     */
    audioRef: Object;

    /**
     * Initializes a new {@code SpeakerEntry} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.audioRef = React.createRef();
        this._onTestButtonClick = this._onTestButtonClick.bind(this);
        this._onClick = this._onClick.bind(this);
        this._playTestSound = this._playTestSound.bind(this);
    }

    _onClick: () => void;

    /**
     * Click handler for the entry.
     *
     * @returns {void}
     */
    _onClick() {
        this.props.onClick(this.props.deviceId);
    }

    _onTestButtonClick: Object => void;

    /**
     * Click handler for Test button.
     * Sets the current audio output id and plays a sound.
     *
     * @param {Object} e - The sythetic event.
     * @returns {void}
     */
    async _onTestButtonClick(e) {
        e.stopPropagation();

        try {
            await this.audioRef.current.setSinkId(this.props.deviceId);
            const element = this.audioRef.current;

            if (!element || !element.isConnected) {
                return;
            }

            this._playTestSound(element, true);
        } catch (err) {
            logger.log('Could not set sink id', err);
        }
    }

    /**
     * Plays the test sound, reloading its source once if the element is stuck in an error state.
     *
     * @param {HTMLAudioElement} element - The test sound element.
     * @param {boolean} allowReload - Whether a NotSupportedError may trigger a reload and a second attempt.
     * @private
     * @returns {void}
     */
    _playTestSound(element, allowReload) {
        safePlayMediaElement(element, {
            onAutoplayBlocked: error => {
                logger.warn('Speaker test sound blocked by browser policy:', error);
            },
            onError: error => {
                // The element preloads its source when the settings popup mounts. If that fetch fails (a dropped
                // or filtered connection) the element stays in MEDIA_ERR_SRC_NOT_SUPPORTED and every play()
                // rejects with "The element has no supported sources" until load() fetches the source again.
                if (allowReload && error?.name === 'NotSupportedError') {
                    const handlers = {};

                    handlers.canplay = () => {
                        element.removeEventListener('error', handlers.error);
                        this._playTestSound(element, false);
                    };
                    handlers.error = () => {
                        element.removeEventListener('canplay', handlers.canplay);
                        logger.error('Speaker test sound play failed:', error);
                    };

                    element.addEventListener('canplay', handlers.canplay, { once: true });
                    element.addEventListener('error', handlers.error, { once: true });
                    element.load();

                    return;
                }

                logger.error('Speaker test sound play failed:', error);
            }
        });
    }

    /**
     * Implements React's {@link Component#render}.
     *
     * @inheritdoc
     */
    render() {
        const { children, isSelected, deviceId } = this.props;

        return (
            <div
                className = 'audio-preview-speaker'
                onClick = { this._onClick }>
                <AudioSettingsEntry
                    isSelected = { isSelected }
                    key = { deviceId }>
                    {children}
                </AudioSettingsEntry>
                <TestButton onClick = { this._onTestButtonClick } />
                <audio
                    preload = 'auto'
                    ref = { this.audioRef }
                    src = { TEST_SOUND_PATH } />
            </div>
        );
    }
}
